import { useEffect, useReducer, useRef, useState } from "react";
import { api, openWS, type CliPayload, type ReadingPayload, type RtpPayload, type StationCard, type WSEvent } from "./lib/api";
import { ageSeconds, formatAge, formatET, formatETDate, formatETTime } from "./lib/time";

function localDate(utcIso: string, tz: string): string {
  // Returns "YYYY-MM-DD" in the given IANA tz.
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
  });
  return fmt.format(new Date(utcIso));
}

type State = {
  stations: StationCard[];
  loading: boolean;
  error: string | null;
};
type Action =
  | { kind: "load_ok"; stations: StationCard[] }
  | { kind: "load_err"; error: string }
  | { kind: "ws_reading"; reading: ReadingPayload }
  | { kind: "ws_cli"; cli: CliPayload }
  | { kind: "ws_rtp"; rtp: RtpPayload };

function reducer(s: State, a: Action): State {
  switch (a.kind) {
    case "load_ok": return { stations: a.stations, loading: false, error: null };
    case "load_err": return { ...s, loading: false, error: a.error };
    case "ws_reading":
      return {
        ...s,
        stations: s.stations.map((st) => {
          if (st.id !== a.reading.station_id) return st;
          let today = st.today_observed;
          // Update running high/low if the new reading is for today's
          // local day in this station's tz. If midnight has rolled
          // over (local_date no longer matches), reset.
          const obsLocalDate = localDate(a.reading.observed_at.utc, st.tz);
          const nowLocalDate = localDate(new Date().toISOString(), st.tz);
          if (today.local_date !== nowLocalDate) {
            today = {
              ...today,
              local_date: nowLocalDate,
              high_f: null,
              low_f: null,
              confirmed_high_f: null,
              confirmed_high_at_utc: null,
              confirmed_low_f: null,
              confirmed_low_at_utc: null,
              latest_synoptic_at_utc: null,
              n_obs: 0,
              latest_obs_at_utc: null,
            };
          }
          if (obsLocalDate === today.local_date && a.reading.temp_f != null) {
            const t = a.reading.temp_f;
            const obsUtc = a.reading.observed_at.utc;
            // Roll up across hourly temp + any synoptic max/min that
            // arrived in this same METAR's remarks. Track which
            // reading set the new extreme so we can display the time
            // and source on the dashboard.
            let {
              high_f, high_at_utc, low_f, low_at_utc,
              confirmed_high_f, confirmed_high_at_utc,
              confirmed_low_f, confirmed_low_at_utc,
              latest_synoptic_at_utc,
            } = today;
            const tryHigh = (v: number | null | undefined) => {
              if (v == null) return;
              if (high_f == null || v > high_f) { high_f = v; high_at_utc = obsUtc; }
            };
            const tryLow = (v: number | null | undefined) => {
              if (v == null) return;
              if (low_f == null || v < low_f) { low_f = v; low_at_utc = obsUtc; }
            };
            tryHigh(t);
            tryHigh(a.reading.temp_6h_max_f);
            tryLow(t);
            tryLow(a.reading.temp_6h_min_f);
            // Synoptic-confirmed extremes: only the 6h synoptic
            // columns count; the running max/min from synoptics is
            // the trader-grade signal.
            if (a.reading.temp_6h_max_f != null) {
              if (confirmed_high_f == null || a.reading.temp_6h_max_f > confirmed_high_f) {
                confirmed_high_f = a.reading.temp_6h_max_f;
                confirmed_high_at_utc = obsUtc;
              }
              latest_synoptic_at_utc = obsUtc;
            }
            if (a.reading.temp_6h_min_f != null) {
              if (confirmed_low_f == null || a.reading.temp_6h_min_f < confirmed_low_f) {
                confirmed_low_f = a.reading.temp_6h_min_f;
                confirmed_low_at_utc = obsUtc;
              }
              latest_synoptic_at_utc = obsUtc;
            }
            today = {
              ...today,
              high_f, high_at_utc,
              low_f, low_at_utc,
              confirmed_high_f, confirmed_high_at_utc,
              confirmed_low_f, confirmed_low_at_utc,
              latest_synoptic_at_utc,
              n_obs: today.n_obs + 1,
              latest_obs_at_utc: obsUtc,
            };
          }
          return { ...st, latest_reading: a.reading, today_observed: today };
        }),
      };
    case "ws_cli":
      // Route the new CLI into cli_today vs cli_yesterday based on
      // its report_for_date relative to today's weather_day in the
      // station's tz.
      return {
        ...s,
        stations: s.stations.map((st) => {
          if (st.id !== a.cli.station_id) return st;
          const todayWd = st.today_observed.local_date;
          const yestWd = (() => {
            const d = new Date(`${todayWd}T00:00:00Z`);
            d.setUTCDate(d.getUTCDate() - 1);
            return d.toISOString().slice(0, 10);
          })();
          if (a.cli.report_for_date === todayWd) return { ...st, cli_today: a.cli };
          if (a.cli.report_for_date === yestWd)  return { ...st, cli_yesterday: a.cli };
          return st;
        }),
      };
    case "ws_rtp":
      return {
        ...s,
        stations: s.stations.map((st) =>
          st.id === a.rtp.station_id ? { ...st, latest_rtp: a.rtp } : st
        ),
      };
  }
}

export function Dashboard() {
  const [state, dispatch] = useReducer(reducer, { stations: [], loading: true, error: null });
  const [wsStatus, setWsStatus] = useState<"connecting" | "open" | "closed">("connecting");
  const [lastHeartbeat, setLastHeartbeat] = useState<number | null>(null);
  const [, force] = useState(0);
  const tickRef = useRef<number | null>(null);

  // Re-render every second so age counters tick.
  useEffect(() => {
    tickRef.current = window.setInterval(() => force((n) => n + 1), 1000);
    return () => { if (tickRef.current) window.clearInterval(tickRef.current); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    function refresh() {
      api.stations()
        .then((stations) => { if (!cancelled) dispatch({ kind: "load_ok", stations }); })
        .catch((e) => { if (!cancelled) dispatch({ kind: "load_err", error: String(e) }); });
    }
    refresh();
    // Poll /api/stations every 60s to pick up backfill, midnight
    // rollovers, and any drift between WS event stream and DB.
    const t = window.setInterval(refresh, 60_000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, []);

  useEffect(() => {
    const ws = openWS(
      (ev: WSEvent) => {
        if (ev.type === "reading") dispatch({ kind: "ws_reading", reading: ev.data });
        else if (ev.type === "cli") dispatch({ kind: "ws_cli", cli: ev.data });
        else if (ev.type === "rtp") dispatch({ kind: "ws_rtp", rtp: ev.data });
        else if (ev.type === "heartbeat") setLastHeartbeat(Date.now());
      },
      (s) => {
        setWsStatus(s);
        // On reconnect, refetch — server may have restarted with new
        // backfill, and we don't want to wait up to 60s for the
        // next interval to catch the truth.
        if (s === "open") {
          api.stations()
            .then((stations) => dispatch({ kind: "load_ok", stations }))
            .catch(() => {});
        }
      }
    );
    return () => ws.close();
  }, []);

  if (state.loading) return <p>Loading stations…</p>;
  if (state.error) return <p style={{ color: "red" }}>Error: {state.error}</p>;

  const heartbeatAge = lastHeartbeat == null ? null : (Date.now() - lastHeartbeat) / 1000;
  const live = wsStatus === "open" && heartbeatAge != null && heartbeatAge < 5;

  return (
    <section>
      <header style={headerStyle}>
        <h2 style={{ margin: 0 }}>Dashboard</h2>
        <span style={live ? statusOk : statusBad}>
          {live
            ? `live (heartbeat ${heartbeatAge!.toFixed(0)}s ago)`
            : wsStatus === "open"
              ? "ws open, no heartbeat yet"
              : `ws ${wsStatus}`}
        </span>
      </header>
      {state.stations.length === 0 ? (
        <p>No active stations seeded.</p>
      ) : (
        <div style={gridStyle}>
          {state.stations.map((st) => (
            <StationCardView key={st.id} station={st} />
          ))}
        </div>
      )}
    </section>
  );
}

function SourceBadge({ source }: { source: string }) {
  // Approximate (HF) gets an amber badge; precise sources get green;
  // unknown gray. Hover-title spells out what the source means.
  const labels: Record<string, { text: string; color: string; bg: string; title: string }> = {
    metar:        { text: "METAR",  color: "#0a0", bg: "#e6f5e6", title: "Hourly METAR (precise to 0.1°F)" },
    synoptic_6h:  { text: "6h SYN", color: "#0a0", bg: "#e6f5e6", title: "6-hour synoptic group from METAR remarks (precise to 0.1°F, sourced from 1-min ASOS sensor)" },
    synoptic_24h: { text: "24h SYN",color: "#0a0", bg: "#e6f5e6", title: "24-hour synoptic group (prior calendar day, precise to 0.1°F)" },
    madis_hf:     { text: "HF",     color: "#a60", bg: "#fdf3da", title: "MADIS 5-min HF feed — approximate (±0.9°F from C round-trip)" },
  };
  const l = labels[source] ?? { text: source, color: "#666", bg: "#eee", title: source };
  return (
    <span
      title={l.title}
      style={{
        marginLeft: "0.4rem",
        padding: "0.05rem 0.35rem",
        fontSize: "0.7rem",
        borderRadius: 3,
        color: l.color,
        background: l.bg,
        fontWeight: 600,
        verticalAlign: "middle",
      }}
    >
      {l.text}
    </span>
  );
}

// Sources that transmit whole °C and round-trip back to °F carry a
// ±0.5°C = ±0.9°F uncertainty band. Returns "(X.X–Y.Y°F)" or null
// if the source is precise.
function uncertaintyBand(value: number, source: string | null): string | null {
  if (source !== "madis_hf") return null;
  return `(${(value - 0.9).toFixed(1)}–${(value + 0.9).toFixed(1)}°F)`;
}

function StationCardView({ station }: { station: StationCard }) {
  const r = station.latest_reading;
  const cliToday = station.cli_today;
  const cliYest = station.cli_yesterday;
  const rtp = station.latest_rtp;
  const t = station.today_observed;
  // Cross-check: ours vs NWS RTP for the same local-day low.
  const rtpLowDelta =
    rtp != null && t.low_f != null && rtp.today_low_f != null && rtp.local_date === t.local_date
      ? t.low_f - rtp.today_low_f
      : null;
  // Cross-check: ours vs today's preliminary CLI for both high and low.
  const cliHighDelta =
    cliToday != null && t.high_f != null && cliToday.high_f != null
      ? t.high_f - cliToday.high_f
      : null;
  const cliLowDelta =
    cliToday != null && t.low_f != null && cliToday.low_f != null
      ? t.low_f - cliToday.low_f
      : null;
  const deltaColor = (d: number | null) =>
    d == null ? "#888" : Math.abs(d) <= 1 ? "#0a0" : "#c00";
  const obsAge = r ? ageSeconds(r.observed_at.utc) : null;
  const recvAge = r ? ageSeconds(r.received_at.utc) : null;

  return (
    <article style={cardStyle}>
      <header style={cardHeaderStyle}>
        <strong style={{ fontSize: "1.1rem" }}>{station.icao}</strong>
        <span style={{ color: "#666" }}>{station.name ?? "—"}</span>
      </header>

      <section>
        <h4 style={sectionH}>Today so far ({t.local_date}, {t.tz.split("/").pop()})</h4>
        <table style={kvTableStyle}>
          <tbody>
            <tr>
              <td>high</td>
              <td>
                <strong>{t.high_f != null ? `${t.high_f.toFixed(1)}°F` : "—"}</strong>
                {t.high_source && <SourceBadge source={t.high_source} />}
                {t.high_at_utc && (
                  <span style={timeAtStyle}>
                    {formatAge(ageSeconds(t.high_at_utc))} ago
                  </span>
                )}
                {t.high_f != null && uncertaintyBand(t.high_f, t.high_source) && (
                  <div style={uncertaintyBandStyle}>
                    {uncertaintyBand(t.high_f, t.high_source)}
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <td>low</td>
              <td>
                <strong>{t.low_f != null ? `${t.low_f.toFixed(1)}°F` : "—"}</strong>
                {t.low_source && <SourceBadge source={t.low_source} />}
                {t.low_at_utc && (
                  <span style={timeAtStyle}>
                    {formatAge(ageSeconds(t.low_at_utc))} ago
                  </span>
                )}
                {t.low_f != null && uncertaintyBand(t.low_f, t.low_source) && (
                  <div style={uncertaintyBandStyle}>
                    {uncertaintyBand(t.low_f, t.low_source)}
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <td>confirmed high</td>
              <td>
                <strong>{t.confirmed_high_f != null ? `${t.confirmed_high_f.toFixed(1)}°F` : "—"}</strong>
                {t.confirmed_high_f != null && <SourceBadge source="synoptic_6h" />}
                {t.confirmed_high_at_utc && (
                  <span style={timeAtStyle}>
                    {formatAge(ageSeconds(t.confirmed_high_at_utc))} ago
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td>confirmed low</td>
              <td>
                <strong>{t.confirmed_low_f != null ? `${t.confirmed_low_f.toFixed(1)}°F` : "—"}</strong>
                {t.confirmed_low_f != null && <SourceBadge source="synoptic_6h" />}
                {t.confirmed_low_at_utc && (
                  <span style={timeAtStyle}>
                    {formatAge(ageSeconds(t.confirmed_low_at_utc))} ago
                  </span>
                )}
              </td>
            </tr>
            <tr><td>obs</td><td>{t.n_obs}</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h4 style={sectionH}>Yesterday (NWS sensor)</h4>
        {station.yesterday_official && station.yesterday_official.weather_day ? (
          <table style={kvTableStyle}>
            <tbody>
              <tr><td>climate day</td><td>{station.yesterday_official.weather_day}</td></tr>
              <tr><td>high</td><td><strong>{station.yesterday_official.high_f != null ? `${station.yesterday_official.high_f.toFixed(1)}°F` : "—"}</strong></td></tr>
              <tr><td>low</td><td><strong>{station.yesterday_official.low_f != null ? `${station.yesterday_official.low_f.toFixed(1)}°F` : "—"}</strong></td></tr>
              <tr><td><span style={{ color: "#888", fontSize: "0.8em" }}>from 06Z 24h synoptic</span></td><td></td></tr>
            </tbody>
          </table>
        ) : (
          <p style={{ color: "#888" }}>no 24h synoptic yet</p>
        )}
      </section>

      <section>
        <h4 style={sectionH}>Today's CLI cross-check (preliminary)</h4>
        {cliToday ? (
          <table style={kvTableStyle}>
            <tbody>
              <tr>
                <td>high (NWS)</td>
                <td>
                  {cliToday.high_f != null ? `${cliToday.high_f.toFixed(1)}°F` : "—"}
                  {cliHighDelta != null && (
                    <span style={{ marginLeft: "0.5rem", color: deltaColor(cliHighDelta) }}>
                      (Δ {cliHighDelta >= 0 ? "+" : ""}{cliHighDelta.toFixed(1)}°F)
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <td>low (NWS)</td>
                <td>
                  {cliToday.low_f != null ? `${cliToday.low_f.toFixed(1)}°F` : "—"}
                  {cliLowDelta != null && (
                    <span style={{ marginLeft: "0.5rem", color: deltaColor(cliLowDelta) }}>
                      (Δ {cliLowDelta >= 0 ? "+" : ""}{cliLowDelta.toFixed(1)}°F)
                    </span>
                  )}
                </td>
              </tr>
              <tr><td>announced</td><td>{formatETTime(cliToday.source_announced_at.utc)} ET</td></tr>
              <tr>
                <td>raw</td>
                <td><a href={cliToday.raw_url} target="_blank" rel="noreferrer">CLI →</a></td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p style={{ color: "#888" }}>no preliminary CLI yet today</p>
        )}
      </section>

      <section>
        <h4 style={sectionH}>NWS RTP cross-check</h4>
        {rtp ? (
          <table style={kvTableStyle}>
            <tbody>
              <tr><td>issued</td><td>{formatETTime(rtp.issued_at.utc)} ET</td></tr>
              <tr>
                <td>today low (NWS)</td>
                <td>
                  {rtp.today_low_f != null ? `${rtp.today_low_f.toFixed(1)}°F` : "—"}
                  {rtpLowDelta != null && (
                    <span style={{ marginLeft: "0.5rem", color: deltaColor(rtpLowDelta) }}>
                      (Δ {rtpLowDelta >= 0 ? "+" : ""}{rtpLowDelta.toFixed(1)}°F)
                    </span>
                  )}
                </td>
              </tr>
              <tr><td>precip today</td><td>{rtp.precip_today_in != null ? `${rtp.precip_today_in}"` : "—"}</td></tr>
              <tr>
                <td>raw</td>
                <td><a href={rtp.raw_url} target="_blank" rel="noreferrer">RTP →</a></td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p style={{ color: "#888" }}>no RTP yet</p>
        )}
      </section>

      <section>
        <h4 style={sectionH}>METAR ({r?.source ?? "—"})</h4>
        {r ? (
          <table style={kvTableStyle}>
            <tbody>
              <tr><td>temp</td><td>{r.temp_f != null ? `${r.temp_f}°F` : "—"}</td></tr>
              <tr><td>dewpoint</td><td>{r.dewpoint_f != null ? `${r.dewpoint_f}°F` : "—"}</td></tr>
              <tr><td>wind</td><td>{r.wind_speed_kt != null ? `${r.wind_speed_kt}kt @ ${r.wind_dir_deg ?? "?"}°` : "—"}</td></tr>
              <tr><td>gust</td><td>{r.wind_gust_kt != null ? `${r.wind_gust_kt}kt` : "—"}</td></tr>
              <tr><td>pressure</td><td>{r.pressure_in != null ? `${r.pressure_in} inHg` : "—"}</td></tr>
              <tr><td>visibility</td><td>{r.visibility_mi != null ? `${r.visibility_mi} mi` : "—"}</td></tr>
              <tr><td>observed</td><td>{formatETTime(r.observed_at.utc)} ET ({obsAge != null ? formatAge(obsAge) : "—"} ago)</td></tr>
              <tr><td>received</td><td>{recvAge != null ? `${formatAge(recvAge)} ago` : "—"}</td></tr>
            </tbody>
          </table>
        ) : (
          <p style={{ color: "#888" }}>no reading yet</p>
        )}
      </section>

      <section>
        <h4 style={sectionH}>Yesterday's CLI (settlement reference)</h4>
        {cliYest ? (
          <table style={kvTableStyle}>
            <tbody>
              <tr><td>for date</td><td>{formatETDate(`${cliYest.report_for_date}T12:00:00Z`)}</td></tr>
              <tr><td>high</td><td><strong>{cliYest.high_f != null ? `${cliYest.high_f}°F` : "—"}</strong></td></tr>
              <tr><td>low</td><td><strong>{cliYest.low_f != null ? `${cliYest.low_f}°F` : "—"}</strong></td></tr>
              <tr><td>precip</td><td>{cliYest.precip_in != null ? `${cliYest.precip_in}"` : "—"}</td></tr>
              <tr><td>snow</td><td>{cliYest.snow_in != null ? `${cliYest.snow_in}"` : "—"}</td></tr>
              <tr><td>announced</td><td>{formatET(cliYest.source_announced_at.utc)}</td></tr>
              <tr>
                <td>raw</td>
                <td><a href={cliYest.raw_url} target="_blank" rel="noreferrer">NWS product →</a></td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p style={{ color: "#888" }}>no CLI for yesterday yet</p>
        )}
      </section>
    </article>
  );
}

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "1rem",
  marginBottom: "0.75rem",
};
const statusOk: React.CSSProperties = { color: "#0a0", fontVariantNumeric: "tabular-nums" };
const statusBad: React.CSSProperties = { color: "#c00", fontVariantNumeric: "tabular-nums" };
const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
  gap: "1rem",
};
const cardStyle: React.CSSProperties = {
  border: "1px solid #ddd",
  borderRadius: 8,
  padding: "1rem",
  background: "#fafafa",
};
const cardHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "0.5rem",
};
const sectionH: React.CSSProperties = {
  margin: "0.75rem 0 0.25rem",
  fontSize: "0.9rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "#555",
};
const kvTableStyle: React.CSSProperties = {
  width: "100%",
  fontSize: "0.9rem",
  fontVariantNumeric: "tabular-nums",
};
const timeAtStyle: React.CSSProperties = {
  marginLeft: "0.4rem",
  fontSize: "0.8em",
  color: "#666",
};
// HF uncertainty band — own line, amber to echo the HF source badge
// and signal "this is the trust boundary on the value above."
// `display: block` + `width: fit-content` always breaks to a new
// line but keeps the pill compact instead of stretching full width.
const uncertaintyBandStyle: React.CSSProperties = {
  marginTop: "0.2rem",
  fontSize: "0.85em",
  fontWeight: 600,
  color: "#a60",
  background: "#fdf3da",
  padding: "0.1rem 0.45rem",
  borderRadius: 3,
  display: "block",
  width: "fit-content",
};
